// src/components/books/BookCard.jsx
import { Box, Typography, Chip, LinearProgress, Stack } from '@mui/material';
import { Link } from 'react-router-dom';
import { API_BASE } from '../../utils/api';


const NOT_FOUND = `${API_BASE}/static/images/book-covers/notFound.jpg`;

// Turn whatever the API gives us into a browser-usable <img src>
const resolveCoverSrc = (val) => {
  const v = (val || '').trim();
  if (!v) return NOT_FOUND;

  // Absolute URLs
  if (v.startsWith('http://') || v.startsWith('https://')) return v;

  // Already a backend-served path
  if (v.startsWith('/static/')) return `${API_BASE}${v}`;
  if (v.startsWith('/')) return `${API_BASE}${v}`;

  // Raw filename → build full URL via API_BASE
  return `${API_BASE}/static/images/book-covers/${v}`;
};

/**
 * Props:
 *  - book: { id|_id, title, author|authorName, genre, coverUrl|coverImage, ... }
 *  - showProgress?: boolean
 *  - onCardClick?: (book) => void   // if present, open modal instead of navigating
 */
export default function BookCard({ book, showProgress = false, onCardClick }) {
  const id = book.id || book._id;
  // Prefer normalized URL from backend; fall back to raw coverImage for legacy data
  const coverValue = book.coverUrl || book.cover_url || book.coverImage;
  const coverSrc = resolveCoverSrc(coverValue);

  // If onCardClick exists, intercept click; else keep original Link behavior
const wrapperProps = onCardClick
  ? {
      component: 'div',
      role: 'button',
      tabIndex: 0,
      onClick: () => onCardClick(book),
      onKeyDown: (e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          onCardClick(book);
        }
      },
    }
  : {
      component: Link,
      to: `/read/${id}?type=book`,
      state: {
        book: {
          ...book,
          id,
          _id: id,
          pdfUrl: `${API_BASE}/api/books/${id}/pdf`,
        },
      },
    };

  return (
    <Box
      {...wrapperProps}
      sx={{
        display: 'flex',
        flexDirection: 'column',
        height: '100%',
        maxWidth: '240px',
        minWidth: '200px',
        textDecoration: 'none',
        cursor: 'pointer',
        '&:hover img': { transform: 'scale(1.03)', boxShadow: 2 },
      }}
    >
      {/* Cover */}
      <Box
        component="img"
        src={coverSrc}
        alt={book.title || 'Book cover'}
        onError={(e) => { e.currentTarget.src = NOT_FOUND; }}
        sx={{
          width: '100%',
          height: '200px',
          objectFit: 'cover',
          borderRadius: 1,
          transition: 'all 0.3s ease',
          mb: 1,
        }}
      />

      {/* Meta */}
      <Box sx={{ p: 1, flexGrow: 1 }}>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {book.title}
        </Typography>

        <Typography
          variant="body2"
          color="text.secondary"
          sx={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}
        >
          {book.author || book.authorName}
        </Typography>

        {book.genre && <Chip label={book.genre} size="small" color="secondary" sx={{ mt: 1, mb: showProgress ? 1 : 0, color: '#1A1A1A' }} />}

        {showProgress && (
          <Stack spacing={1} sx={{ mt: 1.5 }}>
            {book.status && (
              <Typography variant="caption" color="primary.main">
                Status: <strong>{book.status}</strong>
              </Typography>
            )}
            {book.progress !== undefined && (
              <>
                <LinearProgress variant="determinate" value={book.progress} sx={{ height: 6, borderRadius: 3 }} />
                <Typography variant="caption" align="right">
                  {book.progress}% complete
                </Typography>
              </>
            )}
            {book.lastRead && (
              <Typography variant="caption" color="text.disabled">
                Last read: {book.lastRead}
              </Typography>
            )}
          </Stack>
        )}
      </Box>
    </Box>
  );
}
